import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { SettingsName } from "@app/class/SettingsName";
import { deepCopy } from "@app/class/Utilities";
import { ICombinedPreferences } from "@app/interfaces/ICombinedPreferences";
import { DynamicReportService } from "@app/main/components/reports/_services/dynamicReports.service";
import { ColumnDataType } from "@app/main/components/reports/enums/ColumnDataType";
import { ReportDataFunction } from "@app/main/components/reports/enums/ReportDataFunction";
import { ReportTableRowDataSource } from "@app/main/components/reports/enums/ReportTableRowDataSource";
import { IColumnHeader } from "@app/main/components/reports/models/IColumnHeader";
import { IDataDefinition } from "@app/main/components/reports/models/IReportDefinition";
import { IReportRow } from "@app/main/components/reports/models/IReportRow";
import { IHighchartsDataSeries } from "@app/services/charts.service";
import { ColourService } from "@app/services/colour.service";
import { DateService } from "@app/services/date.service";
import { SessionService } from "@app/services/session.service";
import { BehaviorSubject, firstValueFrom, map } from "rxjs";
import { IChartSeries } from "./_models/IChartSeries";
import { IDashboardWidget } from "./_models/IDashboardWidget";

@Injectable({
    providedIn: 'root'
})
export class DashboardService {
    private userPrefs: ICombinedPreferences;

    private showFilterBarSubject = new BehaviorSubject<boolean>(true);
    showFilterBar$ = this.showFilterBarSubject.asObservable();

    constructor(
        private readonly dynamicReportService: DynamicReportService,
        private readonly colourService: ColourService,
        private readonly dateService: DateService,
        private readonly http: HttpClient,
        private readonly sessionService: SessionService,
    ) {
        this.userPrefs = this.sessionService.getItem<ICombinedPreferences>(SettingsName.UserOptions);
     }

    dataSourceToWidgetDataDefinition(widget: IDashboardWidget): void {
        widget.dataDefinition ??= {};
    
        widget.dataDefinition.RowDataSource = 
            widget.DataWidgetParameters.RowDataSource ?? ReportTableRowDataSource.Day;
        widget.dataDefinition.TableDefinition = {
            Headers: deepCopy(widget.DataWidgetParameters.DataSource)
        };
    }

    convertDataDefinitionToChartSeries(dataDefinition: IDataDefinition, hideEmptySeries: boolean, useTotals: boolean): IHighchartsDataSeries[] {
        let dataSeries: IHighchartsDataSeries[] = [];
        const headers = deepCopy(dataDefinition.TableDefinition.Headers).slice(1);

        headers.forEach((header, index) => {
            dataSeries.push({
                name: header.ColumnDefinition?.TranslatedTitle ?? header.Title,
                data: [],
                color: this.colourService.getColour(index, false).solidColour
            });
        });

        const rows = useTotals ? [dataDefinition.TableDefinition.TotalsRow] : dataDefinition.TableDefinition.Rows || [];
        rows.forEach(row => this.processDataDefinitionRow(row, headers, dataSeries));

        return hideEmptySeries 
            ? dataSeries.filter(series => series.data.reduce((sum, [, value]) => sum + value, 0) !== 0) 
            : dataSeries;
    }

    aggregateChartSeries(series: IChartSeries<number>[], dataFunction: ReportDataFunction, round = true): number {
        return this.aggregateData(series.map(s => s.value), dataFunction, round);
    }

    aggregateData(data: number[], dataFunction: ReportDataFunction, round = true): number {
        if (!data.length) return 0;
    
        switch (dataFunction) {
            case ReportDataFunction.First:
                return data[0];
            case ReportDataFunction.Last:
                return data[data.length - 1];
            case ReportDataFunction.Count:
                return data.length;
        }
    
        const validData = data.filter(y => !isNaN(y));
        const sum = validData.reduce((x, y) => x + y, 0);
    
        if (dataFunction === ReportDataFunction.Average) {
            const average = sum / validData.length;
            return round ? Math.round(average) : average;
        }
    
        return sum; // Default case: ReportDataFunction.Sum
    }

    convertDataDefinitionToMetricSeries<T = any>(dataDefinition: IDataDefinition, useTotalsRow = false): IChartSeries<T>[] {
        if (!dataDefinition.TableDefinition.Rows) return [];
    
        return dataDefinition.TableDefinition.Rows
            .filter(row => row.HasData && row.Cells.length >= 2) // Filter valid rows
            .map(row => this.createChartSeries(dataDefinition, row)); // Create chart series
    }

    findColumn(dataSource: string, columns: IColumnHeader[], dataSourceType?: string): IColumnHeader {
        const lowerDataSource = dataSource.toLowerCase();
        const lowerDataSourceType = dataSourceType?.toLowerCase();
    
        const columnsToSearch = lowerDataSourceType 
            ? columns.filter(column => column.DataSourceType.toLowerCase().includes(lowerDataSourceType))
            : columns;
    
        const foundColumn = columnsToSearch.find(header => 
            header.DataSourceValue.toLowerCase() === lowerDataSource || 
            header.Title.toLowerCase() === lowerDataSource
        );
    
        if (!foundColumn) {
            console.error("Couldn't find column", dataSource, dataSourceType);
            return null;
        }
    
        return deepCopy(foundColumn);
    }

    async populateDataDefinition(dataDefinition: IDataDefinition): Promise<IDataDefinition> {
        dataDefinition.DistanceMeasurement = this.userPrefs.DistanceMeasurement.Value;
        dataDefinition.VolumeMeasurement = this.userPrefs.VolumeMeasurement.Value;
        dataDefinition.TemperatureMeasurement = this.userPrefs.TemperatureMeasurement.Value;
    
        this.dynamicReportService.formatParametersToUTCTimezone(dataDefinition.Parameters);
    
        const request = this.http.post<IDataDefinition>('/api/analytical/populate', dataDefinition).pipe(
          map((result: IDataDefinition) => {
            this.dynamicReportService.formatParametersToLocalTimezone(result.Parameters);

            result.TableDefinition.Rows.forEach((row, rowIndex) => { row.id = rowIndex });

            return result;
          })
        );

        return await firstValueFrom(request);
    }

    setShowFilterBar(value: boolean): void {
        this.showFilterBarSubject.next(value);
    }

    //--

    private createChartSeries(dataDefinition: IDataDefinition, row: IReportRow): IChartSeries<any> {
        const header = dataDefinition.TableDefinition.Headers[0];
        const value = this.convertDataValue(row.Cells[1].Data, header);
    
        const date = new Date(row.Cells[0].Data);
        date.setHours(0, 0, 0, 0); // Set to the start of the day (00:00:00)
    
        return {
            date: date,
            value: value
        };
    }

    private processDataDefinitionRow(row: IReportRow, headers: IColumnHeader[], dataSeries: IHighchartsDataSeries[]): void {
        if (!row?.HasData) return;
    
        const date = new Date(row.Cells[0].Data).getTime();
    
        row.Cells.slice(1).forEach((cell, index) => {
            const header = headers[index];
            const value = this.convertDataValue(cell.Data, header);
    
            dataSeries[index].data.push([date, value]);
        });
    }

    private convertDataValue(value: any, header: IColumnHeader): any {
        if (value === undefined) return 0;
    
        const { Type, Format } = header.ColumnDefinition;
    
        switch (Type) {
            case ColumnDataType.Double:
            case ColumnDataType.Int:
            case ColumnDataType.Score:
                if (value && Format.RoundTo) {
                    return Math.round((value + Number.EPSILON) * 100) / 100;
                }
                return value;
    
            case ColumnDataType.Duration:
                return this.dateService.parseTimeSpan(value);
        }
    
        return value; // Default return
    }
}
